import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../../services/api';

const Step3_Preview = ({ clinicId, qaItems: initialItems, onBack, onNext, error, setError }) => {
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!clinicId || !initialItems?.length) return;
      setLoading(true);
      try {
        const qaRes = await clinicService.getClinicQAItems(clinicId);
        const qaList = qaRes?.qa_items || qaRes?.items || (Array.isArray(qaRes) ? qaRes : []);

        const withExisting = initialItems.map((item) => {
          const existing = qaList.find(
            (q) => q.question === item.question && (q.language_code || q.language) === item.language
          );
          return {
            ...item,
            existingId: existing?.id,
            existingAnswer: existing?.answer,
          };
        });
        setItems(withExisting);
        setSelectedIds(new Set(withExisting.map((_, i) => i)));
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to load QA');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clinicId, initialItems, setError]);

  const handleToggle = useCallback((idx) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const handleApplyAll = useCallback(() => {
    setSelectedIds(new Set(items.map((_, i) => i)));
  }, [items]);

  const handleClearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleEdit = useCallback((idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, []);

  const handleTagsChange = useCallback((idx, tagNames) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], tags: Array.isArray(tagNames) ? tagNames : [tagNames] };
      return next;
    });
  }, []);

  const handleRemove = useCallback((idx) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
  }, []);

  const handleNext = useCallback(() => {
    if (selectedIds.size === 0) {
      setError('Please select at least one QA item');
      return;
    }
    setError('');
    const selected = items
      .filter((_, i) => selectedIds.has(i))
      .map(({ question, answer, tags, language }) => ({
        language,
        question,
        answer,
        tags: tags || ['Appointment Flow'],
      }));
    onNext({ qaItems: selected });
  }, [items, selectedIds, onNext, setError]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
        <p className="text-gray-600">Loading current QA and tags...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Preview & Edit</h2>
        <p className="text-gray-600">
          Tick items to include. Edit question/answer. Set tags. {selectedIds.size} of {items.length} selected.
        </p>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleApplyAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Apply All
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
          >
            Clear All
          </button>
        </div>
        <span className="text-sm text-gray-600">
          {selectedIds.size} of {items.length} selected
        </span>
      </div>

      <div className="space-y-4 max-h-[32rem] overflow-y-auto">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={`border rounded-lg p-4 ${selectedIds.has(idx) ? 'bg-white' : 'bg-gray-50 opacity-75'}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedIds.has(idx)}
                onChange={() => handleToggle(idx)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Question</label>
                  <input
                    type="text"
                    value={item.question}
                    onChange={(e) => handleEdit(idx, 'question', e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Answer</label>
                  <textarea
                    value={item.answer}
                    onChange={(e) => handleEdit(idx, 'answer', e.target.value)}
                    className="w-full p-2 border rounded text-sm min-h-[80px]"
                    rows={4}
                  />
                </div>
                {item.existingId && (
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>
                      <label className="block font-medium text-gray-600 mb-1">Original</label>
                      <div className="p-2 bg-gray-100 rounded text-gray-700 whitespace-pre-wrap max-h-24 overflow-y-auto">
                        {item.existingAnswer || '(empty)'}
                      </div>
                    </div>
                    <div>
                      <label className="block font-medium text-gray-600 mb-1">Updated</label>
                      <div className="p-2 bg-blue-50 rounded text-gray-700 whitespace-pre-wrap max-h-24 overflow-y-auto">
                        {item.answer}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-500">Tags (comma-separated):</label>
                  <input
                    type="text"
                    value={Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || '')}
                    onChange={(e) =>
                      handleTagsChange(
                        idx,
                        e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                      )
                    }
                    placeholder="Appointment Flow"
                    className="p-1 border rounded text-sm flex-1 max-w-xs"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(idx)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={selectedIds.size === 0}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next ({selectedIds.size} items)
        </button>
      </div>
    </div>
  );
};

export default Step3_Preview;
